import { useCallback, useMemo, useRef } from 'react';

import { Button, Icons, notify, Stack } from '@cast/design-system';
import {
  KentContinuousRebalancingFeatureConfig,
  KentFeatureTypeEnum,
} from '@cast/types-kent';

import { Link } from 'components/router';
import { useConfigureFeatureMutation } from 'hooks/mutations/kent/useConfigureFeatureMutation';
import { useGetFeaturesCompatibilityQuery } from 'hooks/queries/kent/useGetFeaturesCompatibilityQuery';
import { useGetFeaturesConfigsQuery } from 'hooks/queries/kent/useGetFeaturesConfigsQuery';
import { notReachable } from 'utils/types/not-reachable';

import { featureTypeToId } from '../_utils/featureTypeToId';
import { makeKentSettingsLink } from '../navigation';
import { ConsolidationSettingsForm } from './_components/ConsolidationSettingsForm';
import { FormValues } from './_types';
import { isKentrollerIncompatibleWithContinuousRebalancing } from './_utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { RffFormRef } from '@cast/shared-rff-ui';
import { isOneClick } from '@cast/constants';

type LoadableData =
  | {
      type: 'loading';
    }
  | {
      type: 'error';
      error: unknown;
      refetch: () => void;
    }
  | {
      type: 'success';
      featureConfig: KentContinuousRebalancingFeatureConfig;
      hasOutdatedKentroller: boolean;
    };

const useLoadableData = (): LoadableData => {
  const { cluster } = useCluster();
  const {
    data: featuresConfigs,
    isLoading: isFeaturesConfigsLoading,
    error: featuresConfigsError,
    refetch: refetchFeaturesConfigs,
  } = useGetFeaturesConfigsQuery({
    clusterId: cluster.id,
    featureId: featureTypeToId(KentFeatureTypeEnum.CONTINUOUS_REBALANCING),
  });
  const {
    data: featuresCompatibility,
    isLoading: isFeaturesCompatibilityLoading,
    error: featuresCompatibilityError,
    refetch: refetchFeaturesCompatibility,
  } = useGetFeaturesCompatibilityQuery({
    params: { clusterId: cluster.id },
  });

  const consolidationFeatureConfig = useMemo(
    () =>
      featuresConfigs?.find(
        (feature) => feature.type === 'CONTINUOUS_REBALANCING'
      ),
    [featuresConfigs]
  );

  const refetch = useCallback(() => {
    refetchFeaturesConfigs();
    refetchFeaturesCompatibility();
  }, [refetchFeaturesConfigs, refetchFeaturesCompatibility]);

  if (isFeaturesConfigsLoading || isFeaturesCompatibilityLoading) {
    return { type: 'loading' };
  }

  if (featuresConfigsError || featuresCompatibilityError) {
    return {
      type: 'error',
      error: featuresConfigsError || featuresCompatibilityError,
      refetch,
    };
  }

  if (!consolidationFeatureConfig) {
    return {
      type: 'error',
      error: new Error(
        'Consolidation feature config not found in API response'
      ),
      refetch,
    };
  }

  return {
    type: 'success',
    featureConfig: consolidationFeatureConfig,
    hasOutdatedKentroller: isKentrollerIncompatibleWithContinuousRebalancing(
      featuresCompatibility?.features ?? []
    ),
  };
};

export const ConsolidationSettings = () => {
  const loadableData = useLoadableData();
  const formRef = useRef<RffFormRef<FormValues>>(null);
  const { cluster } = useCluster();

  const configureFeatureMutation = useConfigureFeatureMutation({
    onSuccess: () => {
      notify.success(`Consolidation settings updated`);
      // since we are staying on the same page, we need to restart the form with the new data
      formRef.current?.form.restart();
    },
  });

  return (
    <Stack gap={12}>
      {!isOneClick() && (
        <Button
          variant="text"
          size="md"
          component={Link}
          to={makeKentSettingsLink({ clusterId: cluster.id })}
          startIcon={<Icons.ArrowLeft />}
        >
          Back to Settings
        </Button>
      )}

      {(() => {
        switch (loadableData.type) {
          case 'loading':
            return <ConsolidationSettingsForm.Skeleton />;
          case 'error':
            return (
              <ConsolidationSettingsForm.Error refetch={loadableData.refetch} />
            );
          case 'success': {
            return (
              <ConsolidationSettingsForm
                ref={formRef}
                hasOutdatedKentroller={loadableData.hasOutdatedKentroller}
                featureConfig={loadableData.featureConfig}
                isPending={configureFeatureMutation.isPending}
                onSubmit={(updatedConfig) => {
                  configureFeatureMutation.mutate({
                    clusterId: cluster.id,
                    featureConfig: {
                      continuousRebalancingConfig: updatedConfig,
                    },
                  });
                }}
              />
            );
          }
          default:
            return notReachable(loadableData);
        }
      })()}
    </Stack>
  );
};
